import { styled } from '@mui/material/styles';
import { Box, Button, Modal, Table, TableCell, TableContainer, TableHead, TableRow, Typography, Chip } from '@mui/material';

// ============================================
// Layout
// ============================================
export const PageWrapper = styled(Box)({
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const EmptyMessage = styled(Typography)({
  textAlign: 'center',
  padding: '60px 0',
  color: '#999',
  fontSize: '14px',
});

// ============================================
// Contract Table
// ============================================
export const ContractTableContainer = styled(TableContainer)({
  marginTop: '10px',
  borderRadius: '5px',
  overflowX: 'auto',
  overflowY: 'auto',
  width: '100%',
  height: 'auto',
  maxHeight: '600px',
  border: '1px solid #e1e1e1',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: '#f1f1f1',
  },
});

export const ContractTable = styled(Table)({
  minWidth: 900,
  tableLayout: 'fixed',
});

export const ContractHead = styled(TableHead)({
  background: '#fafbfc',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  borderBottom: '1px solid #ddd',
});

export const ContractHeadRow = styled(TableRow)({
  height: '41px',
});

export const ContractHeadCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }) => ({
  borderRight: '1px solid #ddd',
  fontWeight: 500,
  fontSize: '14px',
  ...(width && { width }),
}));

export const ContractBodyRow = styled(TableRow)({
  height: '40px',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
});

export const ContractBodyCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  fontSize: '14px',
});

// ============================================
// Status Chip
// ============================================
const STATUS_COLORS = {
  COMPLETED: { bg: '#e8f5e9', color: '#2e7d32' },
  PENDING: { bg: '#fff3e0', color: '#e65100' },
  CANCELED: { bg: '#ffebee', color: '#c62828' },
};

export const StatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => {
  const colors = STATUS_COLORS[status] || { bg: '#f5f5f5', color: '#616161' };
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '12px',
    height: '24px',
  };
});

// ============================================
// Action Buttons
// ============================================
export const PdfButton = styled(Button)({
  backgroundColor: '#0F7F85',
  color: '#fff',
  borderRadius: '8px',
  minWidth: 'auto',
  padding: '4px 12px',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: '#0d6b70',
  },
});

// ============================================
// Detail Modal
// ============================================
export const StyledModal = styled(Modal)({});

export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 24px',
  borderRadius: '8px 8px 0 0',
  backgroundColor: '#262626',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: 'white',
});

export const CloseIconWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
});

export const ModalContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  backgroundColor: '#fafbfc',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
  },
});

export const ModalFooter = styled(Box)({
  padding: '16px 24px',
  backgroundColor: '#fafbfc',
  borderRadius: '0 0 8px 8px',
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
});

export const DetailRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
});

export const DetailLabel = styled(Typography)({
  width: '120px',
  minWidth: '120px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#555',
});

export const DetailValue = styled(Typography)({
  flex: 1,
  fontSize: '13px',
  color: '#222',
});

export const SectionTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#333',
  paddingBottom: '8px',
  borderBottom: '1px solid #e1e1e1',
});

export const ReceiptItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '6px',
  backgroundColor: '#fff',
  border: '1px solid #e1e1e1',
});

export const ReceiptLink = styled(Button)({
  minWidth: 'auto',
  padding: '2px 10px',
  fontSize: '12px',
  borderRadius: '6px',
  color: '#0F7F85',
  border: '1px solid #0F7F85',
  '&:hover': {
    backgroundColor: '#f0f9fa',
  },
});
